import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@/components/Autocomplete";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem/ListItem";
import { SOME_USERS_QUERY } from "@/pages/people/queries";

export type ReviewerOption = {
  id: string;
  name: string;
  email: string;
};

interface AssignReviewerDialogProps {
  open: boolean;
  onClose: () => void;
  onAssign: (reviewer: ReviewerOption) => void;
  saving?: boolean;
  currentReviewer?: ReviewerOption | null;
}

export default function AssignReviewerDialog({
  open,
  onClose,
  onAssign,
  saving = false,
  currentReviewer
}: AssignReviewerDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedReviewer, setSelectedReviewer] = useState<ReviewerOption | null>(currentReviewer ?? null);
  const autocompleteWrapperRef = useRef<HTMLDivElement>(null);

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm },
    skip: !debouncedSearchTerm
  });

  useEffect(() => {
    if (open) {
      setSelectedReviewer(currentReviewer ?? null);
      setSearchTerm(currentReviewer?.name ?? "");
      setDebouncedSearchTerm("");
    }
  }, [open, currentReviewer]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const syncMenuWidth = useCallback(() => {
    const wrapper = autocompleteWrapperRef.current;
    if (!wrapper) return;

    const mdMenu = wrapper.querySelector("md-menu") as HTMLElement | null;
    const anchor = wrapper.querySelector(".relative");

    if (mdMenu && anchor) {
      const width = anchor.getBoundingClientRect().width;
      if (width > 0) {
        mdMenu.style.minWidth = `${width}px`;
        mdMenu.style.width = `${width}px`;
        mdMenu.style.maxWidth = `${width}px`;
      }
    }
  }, []);

  useEffect(() => {
    if (usersData) {
      requestAnimationFrame(syncMenuWidth);
    }
  }, [usersData, syncMenuWidth]);

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Assign Reviewer"
      className="w-[560px] max-w-[95vw]"
      hasFormElements
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={() => selectedReviewer && onAssign(selectedReviewer)}
            disabled={saving || !selectedReviewer}
            testId="assign-reviewer-save"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="max-h-[calc(85vh-12rem)] overflow-y-auto min-h-0" data-testid="assign-reviewer-dialog-scroll">
        <div ref={autocompleteWrapperRef} className="pr-1">
          <Autocomplete<ReviewerOption>
            label="Reviewer"
            menuPositioning="fixed"
            query={selectedReviewer ? selectedReviewer.name : searchTerm}
            loadingResults={usersLoading}
            data={usersData?.backofficeUsers || []}
            onChangeText={(value) => {
              if (selectedReviewer) {
                setSelectedReviewer(null);
              }
              setSearchTerm(value);
            }}
            reset={() => {
              setSelectedReviewer(null);
              setSearchTerm("");
              setDebouncedSearchTerm("");
            }}
            onSelect={(user: ReviewerOption) => {
              setSelectedReviewer(user);
              setSearchTerm(user.name);
            }}
            renderItem={(user: ReviewerOption) => (
              <ListItem compact useAvatar iconName="person" headline={user.name} supportingText={user.email} />
            )}
          />
        </div>
      </div>
    </Dialog>
  );
}
